// src/components/SearchComponent/SEOInternalLinks/SEOInternalLinksUtils.ts
import { FACETS_YEARS_COUNT, LAST_FACET_YEAR } from "@globalConfig";
import brandTreeAr from "@data/lookups/brands_tree_ar.json";
import brandTreeEn from "@data/lookups/brands_tree_en.json";
import { useLang } from "@/common/Contexts/LanguageContext";
import { ucWords } from "@/utils/stringHelpers";
var START_YEAR = LAST_FACET_YEAR - FACETS_YEARS_COUNT;
var END_YEAR = LAST_FACET_YEAR;

// Types

/**
 * Determine page type based on selected options
 */
export var determinePageType = function determinePageType() {
  var selectedOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  if (selectedOptions.length === 0) return null;
  var hasMake = selectedOptions.some(function (option) {
    return option.filterField === 'make_id';
  });
  var hasModel = selectedOptions.some(function (option) {
    return option.filterField === 'model_id';
  });
  var hasYear = selectedOptions.some(function (option) {
    return option.filterField === 'year';
  });
  if (hasYear) {
    return 'year';
  } else if (hasModel && hasMake) {
    return 'model';
  } else if (hasMake) {
    return 'make';
  }
  return null;
};

/**
 * Filter makes based on current make parameter
 */
export var filterMakes = function filterMakes(allMakes, currentMakeId) {
  return allMakes.filter(function (makeObj) {
    return makeObj.id != currentMakeId;
  });
};

/**
 * Filter models based on current model parameter
 */
export var filterModels = function filterModels(allModels, currentMakeId, currentModelId) {
  return allModels.filter(function (modelObj) {
    return modelObj.id != currentModelId && modelObj.make_id == currentMakeId;
  });
};

/**
 * Generate years array
 */
export var generateYearsArray = function generateYearsArray() {
  return Array.from({
    length: END_YEAR - START_YEAR + 1
  }, function (_, index) {
    return END_YEAR - index;
  });
};

/**
 * Helper to create section metadata objects
 */
export var createSectionMetadata = function createSectionMetadata(filteredMakes, filteredModels, years) {
  var metadata = {
    seoData: {
      makes: filteredMakes
    }
  };
  if (filteredModels) {
    metadata.seoData.models = filteredModels;
  }
  if (years) {
    metadata.seoData.years = years;
  }
  return metadata;
};

/**
 * Process SEO links data (main utility function)
 */
export var processSEOLinksData = function processSEOLinksData() {
  var _filters$find, _filters$find$values, _filters$find$values$, _filters$find2, _filters$find2$values, _filters$find2$values2, _selectedOptions$find, _selectedOptions$find2;
  var selectedOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var filters = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var routeParams = arguments.length > 2 ? arguments[2] : undefined;
  var make = routeParams.make,
    model = routeParams.model,
    year = routeParams.year;
  var lang = useLang();
  var allMakesData = lang == 'ar' ? brandTreeAr.makes : brandTreeEn.makes;
  var allModelsData = lang == 'ar' ? brandTreeAr.models : brandTreeEn.models;
  var allModels = ((_filters$find = filters.find(function (option) {
    return option.filterField === 'model_id';
  })) === null || _filters$find === void 0 ? void 0 : (_filters$find$values = _filters$find.values) === null || _filters$find$values === void 0 ? void 0 : (_filters$find$values$ = _filters$find$values[0]) === null || _filters$find$values$ === void 0 ? void 0 : _filters$find$values$.values) || [];
  var allMakes = ((_filters$find2 = filters.find(function (option) {
    return option.filterField === 'make_id';
  })) === null || _filters$find2 === void 0 ? void 0 : (_filters$find2$values = _filters$find2.values) === null || _filters$find2$values === void 0 ? void 0 : (_filters$find2$values2 = _filters$find2$values[0]) === null || _filters$find2$values2 === void 0 ? void 0 : _filters$find2$values2.values) || [];

  // Process data using utility functions
  var pageType = determinePageType(selectedOptions);
  var makeId = (_selectedOptions$find = selectedOptions.find(function (option) {
    return option.filterField === 'make_id';
  })) === null || _selectedOptions$find === void 0 ? void 0 : _selectedOptions$find.id;
  var modelId = (_selectedOptions$find2 = selectedOptions.find(function (option) {
    return option.filterField === 'model_id';
  })) === null || _selectedOptions$find2 === void 0 ? void 0 : _selectedOptions$find2.id;
  var filteredMakes = makeId ? filterMakes(allMakesData, makeId) : allMakesData;
  var filteredModels = modelId ? filterModels(allModelsData, makeId, modelId) : allModelsData;

  //convert seo_url,seo_name to name_en in filteredMakes and filteredModels
  filteredMakes.forEach(function (make) {
    make.name_en = make.seo_url;
    make.name = ucWords(make.name);
  });
  filteredModels.forEach(function (model) {
    model.name_en = model.seo_name;
    model.name = ucWords(model.name);
  });
  var selectedOptionsExcludingMakes = selectedOptions.filter(function (option) {
    return option.filterField !== 'make_id' && option.filterField !== 'model_id';
  });
  var selectedOptionsExcludingModels = selectedOptions.filter(function (option) {
    return option.filterField !== 'model_id';
  });
  var yearNum = parseInt(year);
  var years = generateYearsArray().filter(function (yearVal) {
    return yearNum === null || yearVal !== yearNum;
  });
  return {
    pageType: pageType,
    filteredMakes: filteredMakes,
    filteredModels: filteredModels,
    years: years,
    selectedOptionsExcludingMakes: selectedOptionsExcludingMakes,
    selectedOptionsExcludingModels: selectedOptionsExcludingModels
  };
};